import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import HeaderWithBack from "../../components/HeaderWithBack";
import BottomNav from "../../components/BottomNav";
import Loading from "../../components/Loading";
import { useProfile } from "../../hooks/useProfile";
import { useAuthStore } from "../../stores/authStores";
import { useQueryClient } from "@tanstack/react-query";
import axios from '../../api/axios';
import { defaultAvatarUrl, imageUrl } from "../../utils/assets";

const DEFAULT_AVATAR_URL = defaultAvatarUrl();

interface ProfileForm {
    name: string;
    birth_date: string;
    gender: string;
    marital_status: string;
    blood_type: string;
    nik: string;
    weight: string;
    height: string;
    phone: string;
    email: string;
}

const ProfileDetail: React.FC = () => {
    const navigate = useNavigate();
    const user = useAuthStore((state) => state.user);
    const token = useAuthStore((state) => state.token);
    const { data: profile, isLoading } = useProfile(user?.id, token);
    const queryClient = useQueryClient();

    const [isSaving, setIsSaving] = useState(false);
    const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
    const [avatarFile, setAvatarFile] = useState<File | null>(null);
    const [avatarPreview, setAvatarPreview] = useState<string>(DEFAULT_AVATAR_URL);

    const [form, setForm] = useState<ProfileForm>({
        name: "",
        birth_date: "",
        gender: "",
        marital_status: "",
        blood_type: "",
        nik: "",
        weight: "",
        height: "",
        phone: "",
        email: "",
    });

    useEffect(() => {
        if (profile) {
            setForm({
                name: profile.name ?? "",
                birth_date: profile.birth_date ?? "",
                gender: profile.gender ?? "",
                marital_status: profile.marital_status ?? "",
                blood_type: profile.blood_type ?? "",
                nik: profile.nik ?? "",
                weight: String(profile.weight ?? ""),
                height: String(profile.height ?? ""),
                phone: profile.phone ?? "",
                email: profile.email ?? "",
            });
            setAvatarPreview(imageUrl(profile.avatar_url, DEFAULT_AVATAR_URL));
        }
    }, [profile]);

    useEffect(() => {
        if (!avatarFile) {
            return;
        }

        const objectUrl = URL.createObjectURL(avatarFile);
        setAvatarPreview(objectUrl);

        return () => URL.revokeObjectURL(objectUrl);
    }, [avatarFile]);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) {
            return;
        }

        setAvatarFile(file);
    };

    const handleSubmit = async () => {
        setIsSaving(true);
        setMessage(null);
        try {
            const payload = new FormData();
            payload.append("name", form.name);
            payload.append("birth_date", form.birth_date);
            payload.append("gender", form.gender);
            payload.append("marital_status", form.marital_status);
            payload.append("blood_type", form.blood_type);
            payload.append("nik", form.nik);
            payload.append("weight", form.weight);
            payload.append("height", form.height);
            payload.append("phone", form.phone);
            payload.append("email", form.email);

            if (avatarFile) {
                payload.append("avatar", avatarFile);
            }

            await axios.post('profile-detail/update', payload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "multipart/form-data",
                },
            });

            // Invalidate cache agar data langsung ter-refresh di halaman Profile
            await queryClient.invalidateQueries({ queryKey: ["profile", user?.id] });

            setMessage({ type: "success", text: "Profil berhasil disimpan!" });

            setTimeout(() => navigate(-1), 1500);
        } catch (error: any) {
            const msg = error?.response?.data?.message ?? "Gagal menyimpan profil.";
            setMessage({ type: "error", text: msg });
            console.error(error);
        } finally {
            setIsSaving(false);
        }
    };

    const inputClass =
        "w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400";
    const labelClass = "block text-xs text-gray-500 mb-1";

    return (
        <div style={{ fontFamily: "Arial, sans-serif", minHeight: "100vh", paddingBottom: "60px" }}>
            <HeaderWithBack title="Edit Profile" />

            {isLoading || !profile ? (
                <div className="flex-1 flex items-center justify-center min-h-screen">
                    <Loading />
                </div>
            ) : (
                <>
                    {/* Avatar Header */}
                    <div className="bg-teal-600 flex flex-col items-center py-6 gap-2">
                        <div className="relative">
                            <img
                                src={avatarPreview}
                                alt="User Avatar"
                                className="w-24 h-24 rounded-full object-cover border-4 border-teal-300"
                            />
                            <label
                                htmlFor="avatar"
                                className="absolute bottom-0 right-0 flex items-center justify-center w-9 h-9 bg-white text-teal-600 rounded-full shadow cursor-pointer border border-teal-100"
                            >
                                <i className="fas fa-camera text-sm"></i>
                            </label>
                            <input
                                id="avatar"
                                type="file"
                                accept="image/png,image/jpeg,image/jpg,image/webp"
                                onChange={handleAvatarChange}
                                className="hidden"
                            />
                        </div>
                        <p className="text-white font-semibold text-base">{profile.name ?? ""}</p>
                        <p className="text-teal-100 text-sm">{profile.email ?? ""}</p>
                    </div>

                    <div className="w-full p-4 md:w-1/3 mx-auto space-y-4">

                        {/* Informasi Pribadi */}
                        <div className="bg-white rounded-lg border shadow p-4">
                            <h4 className="text-teal-600 font-semibold text-sm mb-3 flex items-center gap-2">
                                <i className="fas fa-user"></i> Informasi Pribadi
                            </h4>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className={labelClass}>Nama Lengkap</label>
                                    <input
                                        className={inputClass}
                                        name="name"
                                        value={form.name}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Tanggal Lahir</label>
                                    <input
                                        className={inputClass}
                                        type="date"
                                        name="birth_date"
                                        value={form.birth_date}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Jenis Kelamin</label>
                                    <select
                                        className={inputClass}
                                        name="gender"
                                        value={form.gender}
                                        onChange={handleChange}
                                    >
                                        <option value="">-- Pilih --</option>
                                        <option value="male">Laki-laki</option>
                                        <option value="female">Perempuan</option>
                                    </select>
                                </div>
                                <div>
                                    <label className={labelClass}>Status Pernikahan</label>
                                    <select
                                        className={inputClass}
                                        name="marital_status"
                                        value={form.marital_status}
                                        onChange={handleChange}
                                    >
                                        <option value="">-- Pilih --</option>
                                        <option value="Belum Menikah">Belum Menikah</option>
                                        <option value="Menikah">Menikah</option>
                                        <option value="Cerai">Cerai</option>
                                    </select>
                                </div>
                                <div>
                                    <label className={labelClass}>Golongan Darah</label>
                                    <select
                                        className={inputClass}
                                        name="blood_type"
                                        value={form.blood_type}
                                        onChange={handleChange}
                                    >
                                        <option value="">-- Pilih --</option>
                                        {["A", "B", "AB", "O"].map((b) => (
                                            <option key={b} value={b}>{b}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className={labelClass}>NIK</label>
                                    <input
                                        className={inputClass}
                                        name="nik"
                                        value={form.nik}
                                        onChange={handleChange}
                                        maxLength={16}
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Berat Badan (kg)</label>
                                    <input
                                        className={inputClass}
                                        type="number"
                                        name="weight"
                                        value={form.weight}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Tinggi Badan (cm)</label>
                                    <input
                                        className={inputClass}
                                        type="number"
                                        name="height"
                                        value={form.height}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Kontak */}
                        <div className="bg-white rounded-lg border shadow p-4">
                            <h4 className="text-teal-600 font-semibold text-sm mb-3 flex items-center gap-2">
                                <i className="fas fa-phone-alt"></i> Kontak
                            </h4>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className={labelClass}>No. Telepon</label>
                                    <input
                                        className={inputClass}
                                        type="tel"
                                        name="phone"
                                        value={form.phone}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Email</label>
                                    <input
                                        className={inputClass}
                                        type="email"
                                        name="email"
                                        value={form.email}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Submit */}
                        <button
                            onClick={handleSubmit}
                            disabled={isSaving}
                            className="w-full bg-teal-600 text-white font-semibold py-3 rounded-full shadow disabled:opacity-50"
                        >
                            {isSaving ? (
                                <><i className="fas fa-spinner fa-spin mr-2"></i>Menyimpan...</>
                            ) : (
                                <><i className="fas fa-save mr-2"></i>SIMPAN PERUBAHAN</>
                            )}
                        </button>

                        {/* Pesan inline sukses / error */}
                        {message && (
                            <div
                                className={`px-4 py-3 rounded-lg text-sm font-medium text-center ${
                                    message.type === "success"
                                        ? "bg-teal-50 text-teal-700 border border-teal-200"
                                        : "bg-red-50 text-red-700 border border-red-200"
                                }`}
                            >
                                <i className={`fas mr-2 ${
                                    message.type === "success"
                                        ? "fa-check-circle"
                                        : "fa-exclamation-circle"
                                }`}></i>
                                {message.text}
                            </div>
                        )}
                    </div>
                </>
            )}

            <BottomNav />
        </div>
    );
};

export default ProfileDetail;
